import React, { useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { InviteUserRequestDTO } from '@/core/api/dtos/request/auth/invite'
import { useTranslation } from '@/core/i18n/useTranslation'
import { AccountSelect } from '@/features/account/components/AccountSelector'
import { RoleSelector } from '@/features/account/components/RoleSelector'
import { Button } from '@/shared/components/atoms/Button'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

interface InviteUserModalProps {
	visible: boolean
	onClose: () => void
	onConfirm: (userData: InviteUserRequestDTO) => void
	isLoading?: boolean
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
	visible,
	onClose,
	onConfirm,
	isLoading = false,
}) => {
	const { t } = useTranslation('common')

	const validationSchema = yup.object().shape({
		account_id: yup
			.number()
			.required(t('validation.required'))
			.min(1, t('validation.required'))
			.typeError(t('validation.required')),
		email: yup
			.string()
			.required(t('validation.required'))
			.email(t('validation.email')),
		firstname: yup
			.string()
			.required(t('validation.required'))
			.max(40, t('validation.maxLength', { max: 40 })),
		lastname: yup
			.string()
			.required(t('validation.required'))
			.max(80, t('validation.maxLength', { max: 80 })),
		role_id: yup
			.number()
			.required(t('validation.required'))
			.min(1, t('validation.required'))
			.typeError(t('validation.required')),
	})

	const form = useForm<InviteUserRequestDTO>({
		resolver: yupResolver(validationSchema) as any,
		defaultValues: {
			account_id: 0,
			email: '',
			firstname: '',
			lastname: '',
			role_id: 0,
		},
	})

	useEffect(() => {
		if (visible) {
			form.reset({
				account_id: 0,
				email: '',
				firstname: '',
				lastname: '',
				role_id: 0,
			})
		}
	}, [visible, form])

	const handleSubmit = (data: InviteUserRequestDTO) => {
		onConfirm(data)
	}

	return (
		<Dialog open={visible} onOpenChange={onClose}>
			<DialogContent
				className="sm:max-w-[400px] p-0 gap-0"
				showCloseButton={false}
			>
				<div className="px-6 py-6 border-b border-border">
					<DialogTitle className="text-base font-semibold">
						{t('account.users.modals.invite.title')}
					</DialogTitle>
				</div>

				<form
					onSubmit={form.handleSubmit(handleSubmit)}
					className="flex flex-col"
				>
					<div className="px-6 py-6 space-y-4">
						<div className="flex flex-col gap-2">
							<Label htmlFor="account_id">
								{t('account.users.modals.edit.account')}
							</Label>
							<AccountSelect
								key={`account-select-invite-${form.watch('account_id')}`}
								value={form.watch('account_id')}
								onChange={(accountId) => {
									form.setValue('account_id', Number(accountId), {
										shouldValidate: true,
									})
								}}
								visible={visible}
							/>
							{form.formState.errors.account_id && (
								<p className="text-sm text-destructive mt-1">
									{form.formState.errors.account_id.message}
								</p>
							)}
						</div>

						<div className="flex flex-col gap-2">
							<Label htmlFor="email">
								{t('account.users.modals.edit.email')}
							</Label>
							<Input
								id="email"
								type="email"
								{...form.register('email')}
								placeholder={t('account.users.modals.edit.enter_email')}
							/>
							{form.formState.errors.email && (
								<p className="text-sm text-destructive mt-1">
									{form.formState.errors.email.message}
								</p>
							)}
						</div>

						<div className="flex flex-col gap-2">
							<Label htmlFor="firstname">
								{t('account.users.modals.edit.first_name')}
							</Label>
							<Input
								id="firstname"
								{...form.register('firstname')}
								placeholder={t('account.users.modals.edit.enter_first_name')}
							/>
							{form.formState.errors.firstname && (
								<p className="text-sm text-destructive mt-1">
									{form.formState.errors.firstname.message}
								</p>
							)}
						</div>

						<div className="flex flex-col gap-2">
							<Label htmlFor="lastname">
								{t('account.users.modals.edit.last_name')}
							</Label>
							<Input
								id="lastname"
								{...form.register('lastname')}
								placeholder={t('account.users.modals.edit.enter_last_name')}
							/>
							{form.formState.errors.lastname && (
								<p className="text-sm text-destructive mt-1">
									{form.formState.errors.lastname.message}
								</p>
							)}
						</div>

						<div className="flex flex-col gap-2 w-full">
							<Label htmlFor="role_id">
								{t('account.users.modals.edit.role')}
							</Label>
							<RoleSelector
								key={`role-select-invite-${form.watch('role_id')}`}
								value={form.watch('role_id')}
								onChange={(roleId) => {
									form.setValue('role_id', Number(roleId), {
										shouldValidate: true,
									})
								}}
								placeholder={t('account.users.modals.edit.select_role')}
							/>
							{form.formState.errors.role_id && (
								<p className="text-sm text-destructive mt-1">
									{form.formState.errors.role_id.message}
								</p>
							)}
						</div>
					</div>

					<Separator />

					<div className="px-6 py-6 flex justify-end gap-3">
						<Button
							type="button"
							variant="outline"
							onClick={onClose}
							className="rounded-full px-6"
							disabled={isLoading}
						>
							{t('actions.cancel')}
						</Button>
						<Button
							type="submit"
							className="rounded-full px-6"
							disabled={isLoading}
						>
							{isLoading
								? t('account.users.modals.invite.inviting')
								: t('account.users.modals.invite.invite_user')}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	)
}

export default InviteUserModal
